import { Component, Input, OnInit } from '@angular/core';
import { Site } from 'src/app/api/site/site';
import { ProductTreeNode } from './product-tree-node';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { SiteApiService } from 'src/app/api/site/siteApiService';
import { AssetApiService } from 'src/app/api/asset/assetApiService';
import { AssetSearchResult } from 'src/app/api/asset/search/assetSearchResult';
import { Location as NavLocation } from '@angular/common';
import { OfferingTreeApiService } from 'src/app/api/offeringTree/offeringTreeApiService';
import { Const } from 'src/app/const';

@Component({
  selector: 'site-product-tree',
  templateUrl: './site-product-tree.component.html',
  styleUrls: ['./site-product-tree.component.css'],
  host: { 'class': 'page-container' },
  standalone: false
})
export class SiteProductTreeComponent implements OnInit {
  site: Site | null = null;
  @Input() siteId: number | null | undefined = null;
  selectedNode: ProductTreeNode | null = null;
  selectedAsset: ProductTreeNode | null = null;

  treeControl = new NestedTreeControl<ProductTreeNode>(node => node.treeNodes);
  dataSource = new MatTreeNestedDataSource<ProductTreeNode>();

  constructor(private location: NavLocation,
    private siteApiService: SiteApiService,
    private offeringTreeApiService: OfferingTreeApiService,
    private assetApiService: AssetApiService) {
  }

  ngOnInit() {
    this.loadSite();
  }

  private loadSite(): void {
    if (!this.siteId) {
      console.error("Site ID is not provided.");
      return;
    }

    this.siteApiService.getSite(this.siteId).subscribe({
      next: (site) => {
        this.site = site;
        const root = ProductTreeNode.fromSite(site);
        this.loadAssetsWithoutCid(root);
        this.dataSource.data = [root];
      },
      error: (error) => {
        console.error('Error loading site or offering tree:', error);
      }
    });
  }

  private loadAssetsWithoutCid(rootNode: ProductTreeNode): void {
    this.assetApiService.searchAssetsForSiteWithoutCid(this.siteId!)
      .subscribe({
        next: (assets) => {
          if (assets.length == 0) {
            return;
          }

          const withoutCidNode = new ProductTreeNode();
          withoutCidNode.name = "CID not available";
          withoutCidNode.type = "offering-tree";
          withoutCidNode.assetsLoaded = true;
          withoutCidNode.offeringTreeLoaded = true;
          withoutCidNode.children = assets
            .map(x => ProductTreeNode.fromAsset(x))
            .sort((x, y) => (x.name ?? '').localeCompare(y.name ?? ''));

          rootNode.children.push(withoutCidNode);

          this.refreshTree();
        },
        error: () => { console.error("Error loading assets for site", this.siteId); }
      });
  }

  hasChild = (_: number, node: ProductTreeNode) => node.hasChildren();

  onNodeSelect(node: ProductTreeNode): void {
    this.selectedNode = node;
    this.selectedAsset = null;

    this.handleOfferingTreeForDisplay(node);

    this.handleOfferingTreeForAssetSearch(node);

    this.handleAssetsHigherInOfferingTreeHierarchy(node);

    this.handleAssetData(node);
  }

  private handleAssetData(node: ProductTreeNode) {
    if (node.type == "asset" && node.underlyingData == null) {
      this.assetApiService.getAsset(node.assetId!)
        .subscribe(x => {
          node.underlyingData = x[0];
        });
    }
  }

  private handleAssetsHigherInOfferingTreeHierarchy(node: ProductTreeNode) {
    if (!node.assetsLoaded && node.level < Const.MAX_OFFERING_TREE_LEVEL) {
      this.assetApiService.searchAssetsForOfferingTree(this.siteId!, node.searchCids)
        .subscribe({
          next: (assets) => this.handleAssetResults(node, assets),
          error: () => {
            console.error("Error loading offering tree", node.underlyingOfferingTree?.cid);
          }
        });
    }
  }

  private handleOfferingTreeForDisplay(node: ProductTreeNode) {
    if (node.type == "offering-tree"
      && !node.offeringTreeLoaded
      && node.level < Const.MAX_OFFERING_TREE_LEVEL) {
      this.offeringTreeApiService.getOfferingTreeNode(node.underlyingOfferingTree?.cid!, false)
        .subscribe({
          next: (offeringTree) => {
            node.children = node.children
              .concat(offeringTree.map(x => ProductTreeNode.fromOfferingTree(x, node.level + 1)))
              .sort((x, y) => (x.name ?? '').localeCompare(y.name ?? ''));
            node.offeringTreeLoaded = true;

            this.refreshTree();
            this.treeControl.expand(node);
          },
          error: () => {
            console.error("Error loading offering tree", node.underlyingOfferingTree?.cid);
          }
        });
    }
  }

  private handleOfferingTreeForAssetSearch(node: ProductTreeNode) {
    if (node.type == "offering-tree"
      && !node.offeringTreeLoaded
      && node.level >= Const.MAX_OFFERING_TREE_LEVEL) {
      this.offeringTreeApiService.getOfferingTreeNode(node.underlyingOfferingTree?.cid!, true)
        .subscribe({
          next: offeringTree => {
            node.loadDataForAssetSearchOnOfferingTree(offeringTree)

            if (!node.assetsLoaded) {
              this.assetApiService.searchAssetsForOfferingTree(this.siteId!, node.searchCids)
                .subscribe({
                  next: (assets) => this.handleAssetResults(node, assets),
                  error: () => { }
                });
            }
          },
          error: () => {
            console.error("Error loading offering tree", node.underlyingOfferingTree?.cid);
          }
        });
    }
  }

  onAssetSelect(node: ProductTreeNode): void {
    this.selectedAsset = node;

    if (node.underlyingData == null) {
      this.assetApiService.getAsset(node.assetId!)
        .subscribe(x => {
          node.underlyingData = x[0];
        });
    }
  }

  private handleAssetResults(node: ProductTreeNode, assets: AssetSearchResult[]): void {
    node.children = node.children
      .concat(assets.map(x => ProductTreeNode.fromAsset(x)))
      .sort((x, y) => (x.name ?? '').localeCompare(y.name ?? ''));
    node.assetsLoaded = true;

    this.refreshTree();
    this.treeControl.expand(node);
  }

  private refreshTree(): void {
    var data = this.dataSource.data;
    this.dataSource.data = [];
    this.dataSource.data = data;
  }

  back(): void {
    this.location.back()
  }
}
